import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { apiClient } from '../lib/api';
import {
  getUserIdFromAccessToken,
  isTokenExpiredOrExpiring,
  readSupabaseTokensFromLocalStorage,
} from '../lib/supabaseAuth';

/**
 * Tenant Context for Multi-Tenant Store Selection
 *
 * This context manages the currently selected tenant (store) across the application.
 * Following React best practices, it uses Context + useState for global state management.
 */

export interface Tenant {
  id: string;
  slug: string;
  name: string;
  groupId: string | null;
  isActive: boolean;
}

export type Feature = 'parts' | 'chemicals';

interface TenantContextValue {
  currentTenant: Tenant | null;
  tenants: Tenant[];
  loading: boolean;
  /** True while report data is loading after a tenant switch. Set by report pages. */
  switching: boolean;
  setSwitching: (v: boolean) => void;
  setCurrentTenant: (tenant: Tenant) => void;
  refreshTenants: () => Promise<void>;
  /** Features the current tenant has enabled (e.g. ['parts', 'chemicals']) */
  tenantFeatures: Feature[];
  /** Features in the user's scope for the current tenant (insurance users only; same as tenantFeatures for others) */
  userFeatureScope: Feature[];
  /** Effective features = tenantFeatures ∩ userFeatureScope. What the user can actually see. */
  effectiveFeatures: Feature[];
  /** Loading state for features (separate from tenant loading) */
  featuresLoading: boolean;
  /** Check if the user has access to a given feature for the current tenant */
  hasFeature: (feature: Feature) => boolean;
}

const TenantContext = createContext<TenantContextValue | undefined>(undefined);

/**
 * Custom hook to access tenant context
 * Throws error if used outside TenantProvider
 */
export function useTenant() {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
}

interface TenantProviderProps {
  children: ReactNode;
  initialTenants?: Tenant[];
  initialTenant?: Tenant;
  /**
   * When true, tenant discovery uses the backend admin API (which supports super-admin access)
   * instead of Supabase permission tables.
   *
   * This is intentionally injected by the AuthProvider layer so tests can still render TenantProvider
   * without requiring AuthProvider.
   */
  isSuperAdmin?: boolean;
}

const STORAGE_KEY = 'selected_tenant_slug';

/**
 * TenantProvider Component
 *
 * Wraps the application and provides tenant selection state to all components.
 * Persists selected tenant to localStorage for consistency across sessions.
 */
export const TenantProvider: React.FC<TenantProviderProps> = ({
  children,
  initialTenants,
  initialTenant,
  isSuperAdmin = false,
}) => {
  const queryClient = useQueryClient();
  const [currentTenant, setCurrentTenantState] = useState<Tenant | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);
  const [tenantFeatures, setTenantFeatures] = useState<Feature[]>([]);
  const [userFeatureScope, setUserFeatureScope] = useState<Feature[]>([]);
  const [effectiveFeatures, setEffectiveFeatures] = useState<Feature[]>([]);
  // Start as `true` so consumers (e.g., ProtectedRoute) wait for the first fetch
  // before checking hasFeature(). Set to false only after a tenant resolves AND
  // features arrive (or the fetch completes with an empty/error result).
  const [featuresLoading, setFeaturesLoading] = useState(true);

  // Prevent duplicate initialization (mount + auth events) and avoid state updates after unmount.
  const initializedRef = React.useRef(false);
  const initializingRef = React.useRef<Promise<void> | null>(null);
  const initTokenRef = React.useRef(0);
  const mountedRef = React.useRef(true);
  // Track if we've already done the super admin re-fetch to avoid race conditions
  const superAdminFetchDoneRef = React.useRef(false);
  // Keep a ref to the latest isSuperAdmin value so auth callbacks don't close over stale state
  const isSuperAdminRef = React.useRef(isSuperAdmin);

  // Update the ref whenever the prop changes
  React.useEffect(() => {
    isSuperAdminRef.current = isSuperAdmin;
  }, [isSuperAdmin]);

  /**
   * Check if auth bypass is active (same logic as AuthProvider)
   */
  const isAuthBypassActive = (): boolean => {
    const isLocalDev =
      typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

    const bypassEnabled =
      (import.meta as any).env?.VITE_TEST_BYPASS_AUTH === '1' ||
      (() => {
        try {
          return localStorage.getItem('TEST_BYPASS_AUTH') === '1';
        } catch {
          return false;
        }
      })();

    return isLocalDev && bypassEnabled;
  };

  /**
   * Fetch available tenants from Supabase based on user's permissions
   */
  const fetchTenants = async () => {
    console.log('[TenantContext] fetchTenants called, isSuperAdmin:', isSuperAdminRef.current);
    try {
      // Ensure credentials exist (shared client throws on missing)
      if (!import.meta.env.VITE_SUPABASE_URL || !import.meta.env.VITE_SUPABASE_ANON_KEY) {
        console.warn('Supabase credentials not configured');
        return [];
      }

      // IMPORTANT: Check bypass BEFORE super admin. In bypass mode isSuperAdmin is
      // also true (set by AuthProvider), but the admin API requires a real auth token
      // which doesn't exist in bypass mode. The bypass path queries Supabase directly
      // using the anon key which works without authentication.
      if (isAuthBypassActive()) {
        console.log('🔓 TENANT BYPASS: Fetching all tenants without auth');
        const { data, error } = await supabase
          .from('tenants')
          .select('id, slug, name, group_id, is_active')
          .eq('is_active', true)
          .order('name')
          .limit(50);

        if (error) {
          console.error('Error fetching tenants in bypass mode:', error);
          return [];
        }

        const fetchedTenants: Tenant[] = (data || []).map((row: any) => ({
          id: row.id,
          slug: row.slug,
          name: row.name,
          groupId: row.group_id,
          isActive: row.is_active,
        }));

        if (fetchedTenants.length === 0) {
          console.warn('No tenants found in database - run seed migrations');
          return [];
        }

        console.log(`🔓 TENANT BYPASS: Found ${fetchedTenants.length} tenants`);
        setTenants(fetchedTenants);
        return fetchedTenants;
      }

      // Super admins: use backend admin API so tenant discovery doesn't depend on Supabase RLS/policy.
      if (isSuperAdminRef.current) {
        console.log('[TenantContext] Using admin API for super admin');
        const response = await apiClient.get('/api/admin/stores');
        if (!response.ok) {
          console.error(
            'Error fetching tenants from admin API:',
            response.status,
            response.statusText,
          );
          return [];
        }

        const data = (await response.json()) as Array<{
          id: string;
          slug: string;
          name: string;
          isActive?: boolean;
          group?: { id: string; slug: string; name: string } | null;
        }>;

        const fetchedTenants: Tenant[] = (data || []).map((row) => ({
          id: row.id,
          slug: row.slug,
          name: row.name,
          groupId: row.group?.id ?? null,
          isActive: row.isActive ?? true,
        }));

        console.log('[TenantContext] Admin API returned', fetchedTenants.length, 'tenants');
        setTenants(fetchedTenants);
        return fetchedTenants;
      }

      // Use API to fetch accessible tenants (respects permissions server-side)
      const response = await apiClient.get('/api/admin/stores');
      if (!response.ok) {
        console.error('[TenantContext] Error fetching tenants from API:', response.status);
        return [];
      }

      const data = (await response.json()) as Array<{
        id: string;
        slug: string;
        name: string;
        isActive?: boolean;
        group?: { id: string; slug: string; name: string } | null;
      }>;

      const fetchedTenants: Tenant[] = (data || []).map((row) => ({
        id: row.id,
        slug: row.slug,
        name: row.name,
        groupId: row.group?.id ?? null,
        isActive: row.isActive ?? true,
      }));

      console.log('[TenantContext] API returned', fetchedTenants.length, 'tenants');
      setTenants(fetchedTenants);
      return fetchedTenants;
    } catch (error) {
      console.error('[TenantContext] Error fetching tenants:', error);
      return [];
    }
  };

  /**
   * Initialize tenant selection on mount
   */
  useEffect(() => {
    mountedRef.current = true;

    const initializeTenant = async () => {
      // Deduplicate concurrent calls (e.g. mount + SIGNED_IN event).
      if (initializingRef.current) return initializingRef.current;

      const token = ++initTokenRef.current;

      initializingRef.current = (async () => {
        console.log(
          '[TenantContext] initializeTenant starting, isSuperAdmin:',
          isSuperAdminRef.current,
        );

        // Gate: skip tenant fetch if session is not aal2 (MFA not yet complete).
        // Auth bypass mode is exempt from this check.
        if (!isAuthBypassActive()) {
          const { accessToken } = readSupabaseTokensFromLocalStorage(
            (import.meta as any).env?.VITE_SUPABASE_URL,
          );
          // Also check the proxy-stored token as fallback
          const proxyToken = (() => {
            try { return localStorage.getItem('afl_access_token') || null; } catch { return null; }
          })();
          const token = accessToken || proxyToken;
          if (token && token.split('.').length === 3) {
            try {
              const decoded = JSON.parse(atob(token.split('.')[1]));
              if (decoded?.aal && decoded.aal !== 'aal2') {
                console.log('[TenantContext] Skipping init — session is not aal2 (aal=' + decoded.aal + ')');
                initializingRef.current = null;
                return;
              }
            } catch { /* proceed if decode fails */ }
          }
        }

        setLoading(true);

        // Clear stale tenant slug before fetching — prevents 403 if the stored slug
        // belongs to a tenant this user doesn't have access to (e.g., different user
        // on same browser). The slug is re-set after we validate against fetched tenants.
        const previousSlug = (() => { try { return localStorage.getItem(STORAGE_KEY); } catch { return null; } })();
        try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }

        // Prefer injected tenants for tests or SSR
        let fetchedTenants: Tenant[] = [];
        if (Array.isArray(initialTenants) && initialTenants.length > 0) {
          setTenants([...initialTenants]);
          fetchedTenants = [...initialTenants];
        } else {
          fetchedTenants = await fetchTenants();
        }

        console.log('[TenantContext] fetchTenants returned', fetchedTenants.length, 'tenants');

        if (!mountedRef.current || token !== initTokenRef.current) return;

        // Try to restore previously selected tenant (saved before fetch)
        let selectedSlug: string | null = previousSlug;

        // Find the tenant to set as current
        let tenantToSet: Tenant | null = null;

        if (selectedSlug) {
          tenantToSet = fetchedTenants.find((t) => t.slug === selectedSlug) || null;
        }

        // Fallback to environment variable or first tenant
        if (!tenantToSet && fetchedTenants.length > 0) {
          if (initialTenant) {
            tenantToSet = initialTenant;
          } else {
            const envSlug = import.meta.env.VITE_TENANT_SLUG as string;
            if (envSlug) {
              tenantToSet = fetchedTenants.find((t) => t.slug === envSlug) || fetchedTenants[0];
            } else {
              tenantToSet = fetchedTenants[0];
            }
          }
        }

        if (!mountedRef.current || token !== initTokenRef.current) return;

        if (tenantToSet) {
          // CRITICAL: Persist to localStorage FIRST, before React state update.
          // React state updates can be interrupted by ProtectedRoute redirects,
          // but localStorage persists across remounts.
          try {
            localStorage.setItem(STORAGE_KEY, tenantToSet.slug);
            console.log('[TenantContext] Persisted tenant to localStorage:', tenantToSet.slug);
          } catch (e) {
            console.warn('[TenantContext] Failed to persist tenant to localStorage:', e);
          }
          setCurrentTenantState(tenantToSet);
        }

        initializedRef.current = true;
        setLoading(false);
        console.log('[TenantContext] Initialization complete, loading=false');
      })().finally(() => {
        // Only clear if this call is still the active in-flight promise
        if (initializingRef.current) initializingRef.current = null;
      });

      return initializingRef.current;
    };

    initializeTenant();

    // If auth state changes (login/logout/refresh), re-fetch tenants so the selector appears
    // without requiring a full page reload.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        // Ignore Supabase SIGNED_OUT if the user is signed in via proxy auth flow
        // (proxy sign-in doesn't create a Supabase client session, so the client
        // fires SIGNED_OUT even though the user is authenticated via API tokens).
        try {
          if (localStorage.getItem('afl_user_id')) {
            console.log('[TenantContext] Ignoring SIGNED_OUT — proxy auth session active');
            return;
          }
        } catch { /* ignore */ }
        setTenants([]);
        setCurrentTenantState(null);
        setLoading(false);
        initializedRef.current = false;
        return;
      }

      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
        // Optional guard: avoid redundant init if we've already done it this session.
        if (event === 'SIGNED_IN' && initializedRef.current) return;
        // Super admins should NOT re-run initializeTenant on token refresh/user update
        // because it would use the stale non-superadmin Supabase RLS path. The separate
        // isSuperAdmin useEffect handles super admin tenant fetching.
        if ((event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') && isSuperAdminRef.current) {
          console.log('[TenantContext] Skipping initializeTenant for super admin on', event);
          return;
        }
        initializeTenant();
      }
    });

    // Re-fetch tenants when proxy auth sign-in completes (no Supabase event fires).
    const handleProxySignIn = () => {
      initializedRef.current = false;
      initializeTenant();
    };
    window.addEventListener('proxy-auth-signed-in', handleProxySignIn);

    return () => {
      mountedRef.current = false;
      // Invalidate any in-flight init so late async completions are ignored.
      initTokenRef.current++;
      subscription.unsubscribe();
      window.removeEventListener('proxy-auth-signed-in', handleProxySignIn);
    };

    // Intentionally run once on mount; `initialTenants`/`initialTenant` are for initial-only setup.
  }, []);

  /**
   * Fetch user's feature access whenever the current tenant changes.
   * Calls GET /api/admin/me/features?tenantId=X to get tenant features + user scope.
   */
  useEffect(() => {
    if (!currentTenant?.id) {
      // No tenant yet — keep featuresLoading true so consumers wait.
      // Don't clear features here; they'll be replaced on next tenant.
      return;
    }

    let cancelled = false;
    setFeaturesLoading(true);

    (async () => {
      try {
        const response = await apiClient.get(
          `/api/admin/me/features?tenantId=${encodeURIComponent(currentTenant.id)}`,
        );

        if (!response.ok) {
          console.warn(
            '[TenantContext] Failed to fetch features, status:',
            response.status,
          );
          if (!cancelled) {
            setTenantFeatures([]);
            setUserFeatureScope([]);
            setEffectiveFeatures([]);
            setFeaturesLoading(false);
          }
          return;
        }

        const data = (await response.json()) as {
          tenantFeatures: string[];
          userFeatureScope: string[];
          effectiveFeatures: string[];
        };

        if (cancelled) return;

        setTenantFeatures((data.tenantFeatures ?? []) as Feature[]);
        setUserFeatureScope((data.userFeatureScope ?? []) as Feature[]);
        setEffectiveFeatures((data.effectiveFeatures ?? []) as Feature[]);
        setFeaturesLoading(false);
      } catch (error) {
        console.error('[TenantContext] Error fetching features:', error);
        if (!cancelled) {
          setTenantFeatures([]);
          setUserFeatureScope([]);
          setEffectiveFeatures([]);
          setFeaturesLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [currentTenant?.id]);

  /**
   * Check if a feature is available for the current user on the current tenant.
   */
  const hasFeature = (feature: Feature): boolean => {
    return effectiveFeatures.includes(feature);
  };

  // If super admin status becomes available after initial mount, re-fetch tenants so the selector appears.
  // This handles the case where the initial fetch via Supabase failed but the admin API would work.
  useEffect(() => {
    console.log(
      '[TenantContext] Super admin effect triggered, isSuperAdmin:',
      isSuperAdmin,
      'tenants:',
      tenants.length,
      'currentTenant:',
      currentTenant?.slug,
    );

    // Reset the flag when super admin status is lost (e.g., sign out)
    if (!isSuperAdmin) {
      superAdminFetchDoneRef.current = false;
      return;
    }

    // Only do this once per super admin session to avoid race conditions
    if (superAdminFetchDoneRef.current) {
      console.log('[TenantContext] Super admin fetch already done, skipping');
      return;
    }

    // If we already have tenants and a current tenant, no need to re-fetch
    if (tenants.length > 0 && currentTenant) {
      console.log(
        '[TenantContext] Already have tenants and currentTenant, skipping super admin fetch',
      );
      return;
    }

    // Trigger a re-init (auth check for super admin happens asynchronously in AuthProvider).
    (async () => {
      // If initializeTenant() is already in-flight, wait for it to complete first.
      // This prevents duplicate /api/admin/stores calls racing each other on reload.
      if (initializingRef.current) {
        try {
          await initializingRef.current;
        } catch {
          // ignore - we'll attempt the super-admin fetch below
        }
      }

      // Re-check after awaiting init (avoid racing state updates)
      if (superAdminFetchDoneRef.current) return;
      if (tenants.length > 0 && currentTenant) return;

      console.log('[TenantContext] Starting super admin re-fetch');

      if (!mountedRef.current) return;

      const fetched = await fetchTenants();
      console.log('[TenantContext] Super admin fetch returned', fetched.length, 'tenants');

      // Only mark as done after successful fetch with results
      if (fetched.length > 0) {
        superAdminFetchDoneRef.current = true;
      }

      if (!mountedRef.current || fetched.length === 0) return;

      // Prefer any existing selection, otherwise fall back to env slug or first tenant.
      let selectedSlug: string | null = null;
      try {
        selectedSlug = localStorage.getItem(STORAGE_KEY);
      } catch {
        // ignore
      }

      const envSlug = (import.meta.env.VITE_TENANT_SLUG as string) || '';
      const tenantToSet =
        (selectedSlug ? fetched.find((t) => t.slug === selectedSlug) : null) ||
        (envSlug ? fetched.find((t) => t.slug === envSlug) : null) ||
        fetched[0];

      if (!mountedRef.current) return;

      console.log('[TenantContext] Super admin setting tenant:', tenantToSet?.slug);
      // Use functional update to only set if not already set (avoids race conditions)
      setCurrentTenantState((prev) => {
        if (prev) {
          console.log(
            '[TenantContext] currentTenant already set to',
            prev.slug,
            ', not overwriting',
          );
          return prev;
        }
        return tenantToSet;
      });
      setLoading(false); // Ensure loading is false after super admin fetch
      try {
        localStorage.setItem(STORAGE_KEY, tenantToSet.slug);
      } catch {
        // ignore
      }
    })();
    // Only depend on isSuperAdmin to avoid re-triggering on state changes from fetchTenants
  }, [isSuperAdmin]); // eslint-disable-line

  /**
   * Update current tenant and persist to localStorage
   * CRITICAL: Also invalidates all queries to prevent cross-tenant data leakage
   */
  const setCurrentTenant = (tenant: Tenant) => {
    // SECURITY: Persist to localStorage FIRST to ensure next API calls use correct tenant
    try {
      localStorage.setItem(STORAGE_KEY, tenant.slug);
      console.log('[TenantContext] localStorage SET to:', tenant.slug, 'verify:', localStorage.getItem(STORAGE_KEY));
    } catch (e) {
      console.warn('Failed to save tenant selection to localStorage:', e);
    }

    // Clear API client session cache to force re-fetch with new tenant headers
    apiClient.clearSessionCache();

    // Clear cached data and trigger refetch for all queries.
    // Use resetQueries() instead of removeQueries() — removeQueries() detaches active
    // useQuery observers, which can leave isLoading stuck at true permanently.
    // resetQueries() clears the data (preventing stale cross-tenant leakage) while
    // keeping observer subscriptions alive so the next fetch resolves normally.
    queryClient.resetQueries();

    // Update UI state
    setCurrentTenantState(tenant);
  };

  /**
   * Refresh tenants list from API
   */
  const refreshTenants = async () => {
    await fetchTenants();
  };

  const value: TenantContextValue = {
    currentTenant,
    tenants,
    loading,
    switching,
    setSwitching,
    setCurrentTenant,
    refreshTenants,
    tenantFeatures,
    userFeatureScope,
    effectiveFeatures,
    featuresLoading,
    hasFeature,
  };

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
};
